import React, { useState, createContext, useContext } from "react";
import {
  Avatar,
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import CompanionSettings from "./CompanionSettings";
import { resetCognitoUserPassword } from "../../lib/api/User";
import { logout } from "../../lib/api/Auth";
import { config } from "@config";
import "./Profile.css";
import { AppBar, Toolbar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface profileContextType {
  activateProfile: boolean;
  setActivateProfile: React.Dispatch<React.SetStateAction<boolean>>; 
}

export const profileContext = createContext<profileContextType>({
  activateProfile: false,
  setActivateProfile: () => {}
});

export const Profile = () => {
  const { userInfo } = useUserInfoContext();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [resultDialogMessage, setResultDialogMessage] = useState("");
  const {activateProfile, setActivateProfile} = useContext(profileContext);

  const handleResetPassword = async () => {
    setConfirmDialogOpen(false);
    try {
      await resetCognitoUserPassword(userInfo);
      setResultDialogMessage(
        "Password reset email sent successfully. You will be logged out."
      );
    } catch (error) {
      setResultDialogMessage(
        "Failed to send password reset email. Please try again."
      );
    } finally {
      setResultDialogOpen(true);
    }
  };

  const handleResultDialogClose = () => {
    setResultDialogOpen(false);
    if (resultDialogMessage.includes("successfully")) {
      logout();
    }
  };

  const handleSettingsClose = () => setActivateProfile(false);

  return (
    <Dialog
      open={activateProfile}
      onClose={handleSettingsClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { maxHeight: '92vh' } }}
      >
      <AppBar sx={{ position: 'relative', bgcolor: 'var(--color-accent, #1976d2)' }}>
        <Toolbar variant="dense" sx={{ minHeight: 44, px: 1 }}>
            <IconButton edge="start" color="inherit" size="small" onClick={handleSettingsClose}>
                <CloseIcon fontSize="small" />
            </IconButton>
            <Typography
              sx={{
                fontFamily: "'Newsreader', Georgia, serif",
                fontWeight: 500,
                fontSize: { xs: "0.95rem", md: "1.1rem" },
                color: "text.primary",
                letterSpacing: "-0.02em",
                ml: 1.5,
                flex: 1
              }}
              variant="h6"
            >
              Settings & Profile
            </Typography>
        </Toolbar>
      </AppBar>
      <Box sx={{ paddingTop: 3 }} />
      <div className="glass-card profile-card" style={{ marginLeft: '30px', marginRight: '30px', padding: '12px 16px' }}>
        {/* User Info Section */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              mr: 1.5,
              bgcolor: 'primary.main',
              fontSize: '1rem',
            }}
          >
            {userInfo?.initial}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>{userInfo?.displayName}</Typography>
          </Box>
        </Box>
      </div>

      <div className="glass-card profile-card" style={{ marginLeft: '30px', marginRight: '30px' }}>
        {/* Identity Details Section */}
        <Typography variant="h6" gutterBottom className="section-title">
          Identity Details
        </Typography>
        <Grid container spacing={2}>
          {userInfo?.name && (
            <Grid item xs={12}>
              <Typography variant="body2" align="left" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                Name
              </Typography>
              <TextField
                fullWidth
                value={userInfo.name}
                InputProps={{
                  readOnly: true,
                }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography variant="body2" align="left" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
              Username / Email
            </Typography>
            <TextField
              fullWidth
              value={userInfo?.email || ""}
              InputProps={{
                readOnly: true,
              }}
            />
          </Grid>
        </Grid>
      </div>

      <div className="glass-card profile-card" style={{ marginLeft: '30px', marginRight: '30px' }}>
        {/* Security Section */}
        <Typography variant="h6" gutterBottom className="section-title">
          Security
        </Typography>
        <Button
          variant="contained"
          onClick={() => setConfirmDialogOpen(true)}
        >
          Reset Password
        </Button>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset your password? You will be logged
            out and will need to check your email to complete the process.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleResetPassword} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={resultDialogOpen} onClose={handleResultDialogClose}>
        <DialogTitle>Password Reset</DialogTitle>
        <DialogContent>
          <DialogContentText>{resultDialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResultDialogClose} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {(config.showCompanionSettings ?? true) && (
        <div className="glass-card profile-card" style={{ marginLeft: '30px', marginRight: '30px' }}>
          {/* Companion Settings Section */}
          <Typography variant="h6" gutterBottom className="section-title">
            {config.showResponseLengthOnly ? "Response Length Settings" : "Companion Settings"}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {config.showResponseLengthOnly
              ? "Configure how long persona responses should be"
              : "Configure the way you chat with your companions"}
          </Typography>
          <CompanionSettings />
        </div>
      )}
    </Dialog>
  );
};

export default Profile;
